import { Injectable, OnDestroy, computed, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Subject, timeout } from 'rxjs';
import { ApiService } from './api.service';
import { AuthSessionService } from './auth-session.service';
import { PhotoPreparationService } from './photo-preparation.service';
import { PhotoLeaseLost, PhotoUploadStore, StoredPhotoUpload, photoFinished } from './photo-upload-store.service';

export interface SavedPhoto {
  id: number | string;
  photo_url: string;
  photo_data: string;
  thumbnail_url?: string | null;
  caption: string | null;
  uploader_name: string;
  created_at: string;
}
export type PhotoState = 'queued' | 'preparing' | 'uploading' | 'waiting' | 'failed' | 'saved' | 'cancelled';
export interface PhotoUpload {
  key: string;
  owner: string;
  referenceId: string;
  modelType: 'Job' | 'Project';
  caption: string;
  name: string;
  file?: File;
  prepared?: File;
  state: PhotoState;
  progress: number;
  attempts: number;
  retryAt: number;
  message: string;
  savedPhoto?: SavedPhoto;
}
interface UploadResponse { success: boolean; message?: string; data?: { photos?: SavedPhoto[] }; }
interface ActiveUpload { item: StoredPhotoUpload; stopped: boolean; stop: () => void; }

@Injectable({ providedIn: 'root' })
export class PhotoUploadService implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly session = inject(AuthSessionService);
  private readonly preparation = inject(PhotoPreparationService);
  private readonly storage = inject(PhotoUploadStore);
  private readonly items = signal<StoredPhotoUpload[]>([]);
  private readonly writing = signal(0);
  private readonly runner = crypto.randomUUID();
  private readonly scope = this.api.baseUrl.replace(/\/$/, '');
  readonly online = signal(navigator.onLine);
  readonly loading = signal(true);
  readonly storageError = signal('');
  readonly otherTab = signal(false);
  readonly uploads = computed(() => this.items().filter(item => item.owner === String(this.api.currentUser()?.id ?? '')));
  readonly pending = computed(() => this.uploads().some(item => !photoFinished(item.state)));
  // Committed queue records survive reload; warn only while accepting new input.
  readonly unsafeToReload = computed(() => this.writing() > 0);
  readonly paused = computed(() => !this.online() || this.session.expired() || !this.api.currentUser() || !!this.storageError());
  private readonly savedSubject = new Subject<{ referenceId: string; modelType: 'Job' | 'Project'; photo: SavedPhoto }>();
  readonly saved = this.savedSubject.asObservable();
  private active?: ActiveUpload;
  private running = false;
  private destroyed = false;
  private suspended = false;
  private syncSequence = 0;
  private heartbeat?: ReturnType<typeof setInterval>;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private readonly poll: ReturnType<typeof setInterval>;
  private readonly channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('nzp-photo-uploads');

  constructor() {
    window.addEventListener('online', this.connectionChanged);
    window.addEventListener('offline', this.connectionChanged);
    window.addEventListener('focus', this.resume);
    window.addEventListener('pagehide', this.pageHidden);
    window.addEventListener('pageshow', this.resume);
    document.addEventListener('visibilitychange', this.resume);
    window.addEventListener('beforeunload', this.beforeUnload);
    if (this.channel) this.channel.onmessage = () => void this.refresh();
    this.poll = setInterval(() => void this.refresh(), 10000);
    effect(() => {
      const owner = String(this.api.currentUser()?.id ?? '');
      const expired = this.session.expired();
      untracked(() => {
        if (this.active && (this.active.item.owner !== owner || expired)) this.active.stop();
        void this.refresh();
      });
    });
  }

  async enqueue(referenceId: string, modelType: 'Job' | 'Project', files: File[], caption: string): Promise<void> {
    const owner = this.currentOwner();
    if (!owner || this.session.expired()) throw new Error('Please sign in before queuing photos.');
    if (!referenceId || !files.length || files.length > 10) throw new Error('Choose between 1 and 10 photos.');
    if (Array.from(caption).length > 300) throw new Error('Photo comments must be 300 characters or fewer.');
    const additions: StoredPhotoUpload[] = files.map(file => {
      const key = crypto.randomUUID();
      return { storageId: this.scope + '|' + owner + '|' + key, scope: this.scope, createdAt: Date.now(),
        key, owner, referenceId, modelType, caption: caption.trim(), name: file.name,
        file, state: 'queued', progress: 0, attempts: 0, retryAt: 0, message: '' };
    });
    this.writing.update(value => value + 1);
    try {
      await this.storage.add(additions); // One atomic transaction, including original files.
      this.storageError.set('');
      this.broadcast();
      void this.refresh();
      // Request persistence when supported. A refusal does not imply saving failed.
      void navigator.storage?.persist?.().catch(() => false);
    } catch (error) {
      throw new Error(this.storageMessage(error));
    } finally { this.writing.update(value => value - 1); }
  }

  async retry(key: string) {
    const item = this.uploads().find(row => row.key === key);
    if (!item || !['failed', 'waiting'].includes(item.state) || item.owner !== this.currentOwner()) return;
    await this.edit(item, { state: 'queued', attempts: 0, retryAt: 0, message: '' });
  }

  async cancel(key: string) {
    const item = this.uploads().find(row => row.key === key);
    if (!item || photoFinished(item.state) || item.owner !== this.currentOwner()) return;
    const changed = await this.edit(item, { state: 'cancelled', file: undefined, prepared: undefined,
      message: item.attempts > 0 ? 'Stopped. A sent photo may already be saved; check the gallery.' : 'Cancelled before upload.' });
    if (changed && this.active?.item.key === key) this.active.stop();
  }

  async dismissFinished() {
    const owner = this.currentOwner();
    if (!owner) return;
    try { await this.storage.dismiss(this.scope, owner); this.broadcast(); await this.refresh(); }
    catch (error) { this.storageError.set(this.storageMessage(error)); }
  }

  async retryStorage() { this.storageError.set(''); await this.refresh(); }

  private async edit(item: StoredPhotoUpload, patch: Partial<PhotoUpload>): Promise<boolean> {
    try {
      await this.storage.change(item, patch);
      this.broadcast();
      await this.refresh();
      return true;
    } catch (error) { this.storageError.set(this.storageMessage(error)); return false; }
  }

  private currentOwner(): string {
    try {
      const token = this.api.accessToken().split('.')[1];
      const payload = JSON.parse(atob(token.replace(/-/g, '+').replace(/_/g, '/')));
      const owner = String(this.api.currentUser()?.id ?? '');
      return owner && String(payload.sub) === owner ? owner : '';
    } catch { return ''; }
  }

  private storageMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'name' in error && error.name === 'QuotaExceededError') return 'Device storage is full. Free some space and try again. Photos were not added to the queue.';
    return error instanceof Error ? error.message : 'Device storage is unavailable. Please try again.';
  }

  private async refresh() {
    if (this.destroyed) return;
    const sequence = ++this.syncSequence;
    const owner = this.currentOwner();
    if (!owner) { this.items.set([]); this.loading.set(false); return; }
    try {
      const rows = await this.storage.load(this.scope, owner);
      if (this.destroyed || sequence !== this.syncSequence || owner !== this.currentOwner()) return;
      const previous = this.items();
      const active = this.active;
      if (active) {
        const current = rows.find(row => row.key === active.item.key);
        if (!current || photoFinished(current.state)) active.stop();
      }
      this.items.set(rows.map(row => row.key === active?.item.key && row.state === 'uploading'
        ? { ...row, progress: previous.find(old => old.key === row.key)?.progress || 0 } : row));
      for (const row of rows) {
        if (row.state === 'saved' && row.savedPhoto && previous.some(old => old.key === row.key && old.state !== 'saved')) {
          this.savedSubject.next({ referenceId: row.referenceId, modelType: row.modelType, photo: row.savedPhoto });
        }
      }
      this.loading.set(false);
      if (!this.storageError()) void this.run();
    } catch (error) { this.loading.set(false); this.storageError.set(this.storageMessage(error)); }
  }

  private async run() {
    if (this.running || this.destroyed || this.suspended || this.paused() || !this.currentOwner()) return;
    if (!this.uploads().some(row => ['queued', 'preparing', 'uploading', 'waiting'].includes(row.state))) {
      this.otherTab.set(false);
      return;
    }
    this.running = true;
    try {
      if (navigator.locks) {
        await navigator.locks.request('nzp-photo-queue:' + this.scope, { ifAvailable: true }, async lock => {
          if (!lock) { this.otherTab.set(true); return; }
          // The browser releases this lock on reload/crash. Holding it proves
          // that a stale persisted lease can be replaced without waiting.
          await this.drain(true);
        });
      } else await this.drain(false);
    } catch (error) {
      if (!this.destroyed) this.storageError.set(this.storageMessage(error));
    } finally { this.running = false; }
  }

  private async drain(browserLockHeld: boolean) {
    let acquired = false;
    try {
      const eligible = this.uploads().some(row => ['queued', 'preparing', 'uploading', 'waiting'].includes(row.state));
      if (!eligible) return;
      acquired = await this.storage.lease(this.scope, this.runner, false, browserLockHeld);
      this.otherTab.set(!acquired);
      if (!acquired || this.destroyed) return;
      this.heartbeat = setInterval(() => {
        void this.storage.lease(this.scope, this.runner, true).then(held => {
          if (!held) this.active?.stop();
        }).catch(error => { this.storageError.set(this.storageMessage(error)); this.active?.stop(); });
      }, 20000);
      while (!this.destroyed && !this.suspended && !this.paused()) {
        const owner = this.currentOwner();
        if (!owner) break;
        const rows = await this.storage.load(this.scope, owner);
        if (owner !== this.currentOwner() || this.paused()) break;
        const item = rows.find(row => ['queued', 'preparing', 'uploading'].includes(row.state) || (row.state === 'waiting' && row.retryAt <= Date.now()));
        if (!item) {
          const times = rows.filter(row => row.state === 'waiting').map(row => row.retryAt);
          if (times.length) {
            clearTimeout(this.retryTimer);
            this.retryTimer = setTimeout(() => void this.refresh(), Math.max(1, Math.min(...times) - Date.now()));
          }
          break;
        }
        await this.process(item);
        await this.refresh();
      }
    } catch (error) {
      if (!(error instanceof PhotoLeaseLost)) this.storageError.set(this.storageMessage(error));
    } finally {
      clearInterval(this.heartbeat);
      this.active?.stop();
      this.active = undefined;
      if (acquired) {
        try { await this.storage.release(this.scope, this.runner); }
        catch (error) { this.storageError.set(this.storageMessage(error)); }
      }
    }
  }

  private async change(item: StoredPhotoUpload, patch: Partial<PhotoUpload>): Promise<StoredPhotoUpload | undefined> {
    const row = await this.storage.change(item, patch, this.runner);
    if (row && !this.destroyed && row.owner === this.currentOwner()) {
      // Leave completion transition to refresh(), which also publishes saved events.
      if (row.state !== 'saved') this.items.update(items => items.map(old => old.key === row.key ? row : old));
    }
    this.broadcast();
    return row;
  }

  private async process(original: StoredPhotoUpload) {
    const active: ActiveUpload = { item: original, stopped: false, stop: () => { active.stopped = true; } };
    this.active = active;
    let item = original;
    try {
      if (!item.prepared) {
        const preparing = await this.change(item, { state: 'preparing', message: '' });
        if (!preparing || photoFinished(preparing.state)) return;
        if (!preparing.file) throw new Error('The local photo file is missing. Remove it and choose the photo again.');
        const file = await this.preparation.prepare(this.asFile(preparing.file, preparing.name));
        if (active.stopped || this.destroyed) return;
        const prepared = await this.change(item, { prepared: file, file: undefined, state: 'queued' });
        if (!prepared || photoFinished(prepared.state)) return;
        item = prepared; // Exact outgoing bytes are committed before any network request.
      }
      if (active.stopped || item.owner !== this.currentOwner() || this.paused() || this.destroyed) return;
      const uploading = await this.change(item, { state: 'uploading', attempts: item.attempts + 1, progress: 0, message: '' });
      if (!uploading || photoFinished(uploading.state) || active.stopped || item.owner !== this.currentOwner() || this.paused()) return;
      item = uploading;
      const photo = await this.transfer(item, active);
      if (!photo || active.stopped || this.destroyed) return;
      await this.change(item, { state: 'saved', progress: 100, file: undefined, prepared: undefined, savedPhoto: photo, message: 'Saved' });
    } catch (error) {
      if (error instanceof PhotoLeaseLost) throw error;
      if (active.stopped || this.destroyed) return;
      const status = error instanceof HttpErrorResponse ? error.status : -1;
      const transient = [0, 408, 429, 500, 502, 503, 504].includes(status) || (error instanceof Error && error.name === 'TimeoutError');
      if (status === 401) {
        this.session.markExpired();
        await this.change(item, { state: 'queued', message: 'Sign in with the same account to resume.' });
      } else if (transient && item.attempts < 3) {
        const retryAfter = error instanceof HttpErrorResponse ? Number(error.headers.get('Retry-After')) : 0;
        const delay = Math.max(2000 * 2 ** Math.max(0, item.attempts - 1), Number.isFinite(retryAfter) ? Math.min(60000, retryAfter * 1000) : 0);
        await this.change(item, { state: 'waiting', retryAt: Date.now() + delay, message: 'Connection interrupted. Retrying automatically.' });
      } else {
        const message = error instanceof HttpErrorResponse ? error.error?.message || 'Upload interrupted. Retry when your connection improves.'
          : error instanceof Error ? error.message : 'Photo upload failed.';
        await this.change(item, { state: 'failed', message });
      }
    } finally { if (this.active === active) this.active = undefined; }
  }

  private transfer(item: StoredPhotoUpload, active: ActiveUpload): Promise<SavedPhoto | null> {
    return new Promise((resolve, reject) => {
      const subscription = this.api.uploadQueuedPhoto(item.referenceId, item.modelType,
        this.asFile(item.prepared!, item.name.replace(/\.[^.]+$/, '') + '.jpg'), item.caption, item.key)
        .pipe(timeout({ each: 90000 })).subscribe({
          next: event => {
            if (active.stopped || this.destroyed) return;
            if (event.type === HttpEventType.UploadProgress) {
              const progress = event.total ? Math.min(100, Math.round(event.loaded * 100 / event.total)) : 0;
              this.items.update(items => items.map(row => row.key === item.key ? { ...row, progress } : row));
            } else if (event.type === HttpEventType.Response) {
              const result = event.body as UploadResponse | null;
              const photo = result?.data?.photos?.[0];
              if (!result?.success || !photo?.id || !photo.photo_url) reject(new Error(result?.message || 'The server did not confirm this photo. Retry to check it.'));
              else resolve(photo);
            }
          }, error: reject
        });
      active.stop = () => { active.stopped = true; subscription.unsubscribe(); resolve(null); };
    });
  }

  private asFile(blob: Blob, name: string): File { return blob instanceof File ? blob : new File([blob], name, { type: blob.type }); }
  private broadcast() { if (!this.destroyed) this.channel?.postMessage(this.scope); }
  private readonly connectionChanged = () => {
    this.online.set(navigator.onLine);
    if (!this.online()) this.active?.stop();
    void this.refresh();
  };
  private readonly resume = () => { this.suspended = false; this.online.set(navigator.onLine); void this.refresh(); };
  private readonly pageHidden = () => {
    this.suspended = true;
    this.active?.stop();
    // Best effort for clean navigation/closure; expiry covers OS termination.
    void this.storage.release(this.scope, this.runner).catch(() => undefined);
  };
  private readonly beforeUnload = (event: BeforeUnloadEvent) => {
    if (this.unsafeToReload()) { event.preventDefault(); event.returnValue = ''; }
  };

  ngOnDestroy() {
    this.destroyed = true;
    this.active?.stop();
    clearInterval(this.poll);
    clearInterval(this.heartbeat);
    clearTimeout(this.retryTimer);
    window.removeEventListener('online', this.connectionChanged);
    window.removeEventListener('offline', this.connectionChanged);
    window.removeEventListener('focus', this.resume);
    window.removeEventListener('pagehide', this.pageHidden);
    window.removeEventListener('pageshow', this.resume);
    document.removeEventListener('visibilitychange', this.resume);
    window.removeEventListener('beforeunload', this.beforeUnload);
    this.channel?.close();
    this.savedSubject.complete();
  }
}
